import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/apiService';
import { HottestChain } from '../types';
import '../styles/hottest-chains.css';

interface Filters {
  date: string;
  limit: number;
  min_premium: number;
}

export const HottestChains: React.FC = () => {
  const [chains, setChains] = useState<HottestChain[]>([]);
  const [filteredChains, setFilteredChains] = useState<HottestChain[]>([]);
  const [chainCount, setChainCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChain, setSelectedChain] = useState<HottestChain | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<string>('');

  // Pagination, Search, Sort
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof HottestChain>('premium');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get EST date string (YYYY-MM-DD) from Date object
  const getESTDateString = (date: Date): string => {
    const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const year = estDate.getFullYear();
    const month = String(estDate.getMonth() + 1).padStart(2, '0');
    const day = String(estDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date in EST
  const getTodayDateEST = (): string => {
    return getESTDateString(new Date());
  };

  // Get last N calendar days in EST
  const getLastCalendarDays = (count: number = 9): string[] => {
    const days: string[] = [];
    const today = new Date();
    const estToday = new Date(today.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    
    for (let i = count - 1; i >= 0; i--) {
      const date = new Date(estToday);
      date.setDate(date.getDate() - i);
      days.push(getESTDateString(date));
    }
    
    return days.sort();
  };

  // Format date for display
  const formatDateForDisplay = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00Z');
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const [filters, setFilters] = useState<Filters>({
    date: getTodayDate(),
    limit: 250,
    min_premium: 1000000,
  });

  // Date picker state for historical data
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Pattern icons mapping
  const patternIcons: Record<string, string> = {
    AGGRESSIVE_SWEEP: '🔥',
    DARK_POOL_ACTIVITY: '🌑',
    SPREAD_HEAVY: '📊',
    HIGH_IV_OPPORTUNITY: '📈',
    STRONG_OI_BUILDUP: '📦',
    BULLISH_POSITIONING: '🟢',
    BEARISH_POSITIONING: '🔴',
    HIGH_LEVERAGE: '⚡',
    EARNINGS_PLAY: '📢',
  };

  // Extract option type from option_symbol (e.g., SPXW251215P06800000 -> PUT)
  const extractOptionType = (optionSymbol: string): string => {
    if (!optionSymbol) return 'N/A';
    const cpMatch = optionSymbol.match(/[CP](?=\d{8}$)/);
    if (cpMatch) {
      return cpMatch[0] === 'C' ? 'CALL' : 'PUT';
    }
    return 'N/A';
  };

  // Extract strike from option_symbol (e.g., SPXW251215P06800000 -> 6800.00)
  const extractStrike = (optionSymbol: string): number => {
    if (!optionSymbol) return 0;
    const match = optionSymbol.match(/[CP](\d{8})$/);
    if (match && match[1]) {
      const strikeStr = match[1];
      // Last 3 digits are decimals, first 5 are integers
      const integer = strikeStr.slice(0, 5);
      const decimal = strikeStr.slice(5);
      return parseFloat(integer + '.' + decimal) || 0;
    }
    return 0;
  };

  // Extract expiry date from option_symbol (e.g., SPXW251215P06800000 -> 2025-12-15)
  const extractExpiryDate = (optionSymbol: string): string => {
    if (!optionSymbol) return 'N/A';
    const match = optionSymbol.match(/(\d{6})[CP]/);
    if (match && match[1]) {
      const yymmdd = match[1];
      const yy = yymmdd.slice(0, 2);
      const mm = yymmdd.slice(2, 4);
      const dd = yymmdd.slice(4, 6);
      return `20${yy}-${mm}-${dd}`;
    }
    return 'N/A';
  };

  // Calculate DTE from expiry date
  const calculateDTE = (expiryDate: string): number => {
    if (!expiryDate || expiryDate === 'N/A') return 0;
    try {
      const expiry = new Date(expiryDate + 'T00:00:00Z');
      const today = new Date();
      const diffTime = expiry.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch {
      return 0;
    }
  };

  const formatCurrency = (value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '$0';
    if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(1)}M`;
    }
    if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(1)}K`;
    }
    return `$${numValue.toFixed(2)}`;
  };

  const formatNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const safeToFixed = (value: any, decimals: number = 2): string => {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    return (isNaN(num) ? 0 : num).toFixed(decimals);
  };

  const formatLastFillDateTime = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      }) + ' EST';
    } catch {
      return 'N/A';
    }
  };

  // Get direction emoji
  const getDirectionEmoji = (bias: string | undefined): string => {
    if (!bias) return '⚪';
    const upperBias = bias.toUpperCase();
    if (upperBias === 'BULLISH') return '📈';
    if (upperBias === 'BEARISH') return '📉';
    return '⚪';
  };

  // Get trader type emoji
  const getTraderEmoji = (traderType: string | undefined): string => {
    if (!traderType) return '👤';
    const upper = traderType.toUpperCase();
    if (upper === 'INSTITUTIONAL') return '🏛️';
    if (upper === 'HEDGE_FUND') return '🦈';
    if (upper === 'PROFESSIONAL') return '💼';
    if (upper === 'MIXED') return '👥';
    return '👤';
  };

  // Get activity level emoji
  const getActivityEmoji = (level: string | undefined): string => {
    if (!level) return '⚪';
    const upper = level.toUpperCase();
    if (upper === 'CRITICAL') return '🔴';
    if (upper === 'HIGH') return '🟠';
    if (upper === 'MEDIUM') return '🟡';
    return '⚪';
  };

  // Get OI trend emoji
  const getOiTrendEmoji = (trend: string | undefined): string => {
    if (!trend) return '➡️';
    const upper = trend.toUpperCase();
    if (upper === 'BUILDING') return '📈';
    if (upper === 'REDUCING') return '📉';
    if (upper === 'STABLE') return '➡️';
    return '❓';
  };

  // Get net flow color
  const getNetFlowColor = (direction: string | undefined): string => {
    if (!direction) return '#9ca3af';
    const upper = direction.toUpperCase();
    if (upper.includes('BUYING')) return '#10b981';
    if (upper.includes('SELLING')) return '#ef4444';
    return '#f59e0b';
  };

  const fetchHottestChains = async (dateParam?: string, isAutoRefresh: boolean = false) => {
    try {
      if (!isAutoRefresh) {
      setLoading(true);
      }
      setError(null);

      const requestParams: any = {
        limit: filters.limit,
        min_premium: filters.min_premium,
      };

      if (dateParam) {
        requestParams.date = dateParam;
      }

      const response = await apiService.getHottestChains(requestParams);

      // Smart merge for auto-refresh
      if (isAutoRefresh && chains.length > 0) {
        const existingMap = new Map(chains.map(c => [c.id, c]));
        
        const mergedChains = response.data.map(newChain => {
          const existingChain = existingMap.get(newChain.id);
          if (existingChain) {
            return { ...existingChain, ...newChain };
          }
          return newChain;
        });

        const newIds = new Set(response.data.map(c => c.id));
        const keptChains = chains.filter(c => !newIds.has(c.id));
        
        const allChains = [...mergedChains, ...keptChains];
        
        setChains(allChains);
        setFilteredChains(allChains);
      } else {
        setChains(response.data);
        setFilteredChains(response.data);
      }

      setChainCount(response.count);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch hottest chains';
      setError(errorMessage);
      console.error('Error fetching hottest chains:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHottestChains();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchHottestChains(undefined, true);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, filters]);

  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle date selection from picker
  const handleDateSelection = async (date: string) => {
    setSelectedDate(date);
    await fetchHottestChains(date);
  };

  // Helper to get activity level from chain
  const getActivityLevel = (chain: HottestChain): string => {
    if (chain.activity_level) return chain.activity_level.toUpperCase();
    if (chain.unusual_activity_level) return chain.unusual_activity_level.toUpperCase();
    const score = chain.activity_score ?? 0;
    if (score >= 80) return 'CRITICAL';
    if (score >= 60) return 'HIGH';
    if (score >= 40) return 'MEDIUM';
    return 'LOW';
  };

  // Search and filter
  useEffect(() => {
    let filtered = chains;

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((chain) => {
        // Ticker and symbol search
        const tickerMatch = chain.ticker_symbol?.toLowerCase().includes(search);
        const symbolMatch = chain.option_symbol?.toLowerCase().includes(search);
        const premiumMatch = chain.premium?.toString().includes(search);
        
        // Direction search (bullish, bearish, neutral)
        const direction = (chain.directional_bias || chain.directionalBias || 'NEUTRAL').toLowerCase();
        const directionMatch = direction.includes(search);
        
        // Activity level search (critical, high, medium, low)
        const activityLevel = getActivityLevel(chain).toLowerCase();
        const activityMatch = activityLevel.includes(search);
        
        return tickerMatch || symbolMatch || premiumMatch || directionMatch || activityMatch;
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Handle date fields
      if (sortField === 'last_fill') {
        const aDate = new Date(aVal as string).getTime();
        const bDate = new Date(bVal as string).getTime();
        return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
      }

      const aNum = typeof aVal === 'string' ? parseFloat(aVal) : Number(aVal);
      const bNum = typeof bVal === 'string' ? parseFloat(bVal) : Number(bVal);

      if (isNaN(aNum) || isNaN(bNum)) {
        return String(aVal).localeCompare(String(bVal)) * (sortDirection === 'desc' ? -1 : 1);
      }

      return sortDirection === 'desc' ? bNum - aNum : aNum - bNum;
    });

    setFilteredChains(sorted);
    setCurrentPage(1);
  }, [chains, searchTerm, sortField, sortDirection]);

  const paginatedData = filteredChains.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredChains.length / rowsPerPage);

  const handleSort = (field: keyof HottestChain) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort icon helper - matches OptionFlowFeed style
  const getSortIcon = (field: keyof HottestChain) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const renderPatterns = (patterns: string[] | undefined) => {
    if (!patterns || patterns.length === 0) {
      return <span style={{ color: '#666' }}>-</span>;
    }

    return (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {patterns.slice(0, 3).map((pattern, idx) => (
          <span
            key={idx}
            style={{
              fontSize: '14px',
              cursor: 'help'
            }}
            title={pattern}
          >
            {patternIcons[pattern as keyof typeof patternIcons] || '🔹'}
          </span>
        ))}
        {patterns.length > 3 && (
          <span style={{ fontSize: '11px', color: '#888' }}>+{patterns.length - 3}</span>
        )}
      </div>
    );
  };

  // Get directional bias color and display
  const getDirectionalBiasDisplay = (chain: HottestChain) => {
    const bias = chain.directional_bias || chain.directionalBias || 'NEUTRAL';
    const upperBias = typeof bias === 'string' ? bias.toUpperCase() : 'NEUTRAL';
    
    let color = '#9ca3af';
    let bgColor = 'rgba(156, 163, 175, 0.15)';
    let emoji = '⚪';
    
    if (upperBias === 'BULLISH') {
      color = '#10b981';
      bgColor = 'rgba(16, 185, 129, 0.15)';
      emoji = '📈';
    } else if (upperBias === 'BEARISH') {
      color = '#ef4444';
      bgColor = 'rgba(239, 68, 68, 0.15)';
      emoji = '📉';
    }
    
    return { color, bgColor, emoji, text: upperBias };
  };

  return (
    <motion.div
      className="hottest-chains-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header Section */}
      <motion.div className="hc-header" layout>
        <div className="hc-header-content">
          <div className="hc-title-section">
            <h1 className="hc-title">🔥 Hottest Chains</h1>
            <p className="hc-subtitle">
              {chainCount} chains {selectedDate ? `on ${formatDateForDisplay(selectedDate)}` : '(Latest data)'}
            </p>
          </div>

          {/* Refresh Status */}
          <div className="refresh-status">
            <motion.button
              className="refresh-button"
              onClick={() => fetchHottestChains()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? '⟳ Syncing...' : '🔄 Refresh'}
            </motion.button>
            {lastRefresh && (
              <span style={{ fontSize: '12px', color: '#888', marginLeft: '12px' }}>
                ✓ {lastRefresh}
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* Controls Section */}
      <motion.div className="hc-controls" layout>
        <div className="controls-row">
          {/* Date Picker */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(30, 33, 48, 0.6)',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <label style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>📅 Date:</label>
            <input
              type="date"
              value={selectedDate || getTodayDateEST()}
              onChange={(e) => e.target.value && handleDateSelection(e.target.value)}
              min={getLastCalendarDays(9)[0]}
              max={getTodayDateEST()}
              style={{
                background: 'rgba(17, 24, 39, 0.8)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '4px',
                padding: '6px 10px',
                color: '#f3f4f6',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            />
            {selectedDate && (
              <button
                onClick={() => {
                  setSelectedDate(null);
                  fetchHottestChains();
                }}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '4px',
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  fontSize: '12px'
                }}
              >
                ✕
              </button>
            )}
          </div>

          <div className="control-group">
            <label>Min Premium</label>
            <select
              value={filters.min_premium}
              onChange={(e) => handleFilterChange('min_premium', parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #10b981',
                backgroundColor: '#0f2818',
                color: '#4ade80',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              <option value={100000}>$100K+</option>
              <option value={500000}>$500K+</option>
              <option value={1000000}>$1M+</option>
              <option value={5000000}>$5M+</option>
              <option value={10000000}>$10M+</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ color: '#ef4444' }}>❌ {error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: 'center', padding: '60px 20px' }}
        >
            <motion.div
              animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            style={{ fontSize: '32px', marginBottom: '16px' }}
          >
            🔄
          </motion.div>
          <p style={{ color: '#888' }}>Loading hottest chains...</p>
        </motion.div>
      )}

      {/* Main Table */}
      {!loading && !error && (
        <motion.div
          className="hc-table-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Pagination */}
          {filteredChains.length > rowsPerPage && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '10px 12px', backgroundColor: 'rgba(16, 185, 129, 0.05)', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  backgroundColor: currentPage === 1 ? 'rgba(107, 114, 128, 0.3)' : '#10b981',
                  color: currentPage === 1 ? '#666' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ← Prev
              </button>
              <span style={{ color: '#4ade80', fontSize: '13px', fontWeight: 'bold' }}>
                Page {currentPage} of {totalPages} | {filteredChains.length} chains
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '6px 12px',
                  backgroundColor: currentPage === totalPages ? 'rgba(107, 114, 128, 0.3)' : '#10b981',
                  color: currentPage === totalPages ? '#666' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Next →
              </button>
            </div>
          )}

          {/* Search Bar */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search ticker, symbol, direction (bullish/bearish), activity (high/critical)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 14px',
                backgroundColor: '#1a1a2e',
                border: '1px solid #16c784',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '14px'
              }}
            />
            <span style={{ color: '#888', fontSize: '12px' }}>
              {paginatedData.length} of {filteredChains.length}
            </span>
          </div>

          <div className="table-container">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell" onClick={() => handleSort('ticker_symbol')}>
                    <div className="table-header-content">Ticker {getSortIcon('ticker_symbol')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('type')}>
                    <div className="table-header-content">Type {getSortIcon('type')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('strike')}>
                    <div className="table-header-content">Strike {getSortIcon('strike')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('premium')}>
                    <div className="table-header-content">Premium {getSortIcon('premium')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('last_fill')}>
                    <div className="table-header-content">Last Fill {getSortIcon('last_fill')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('activity_score')}>
                    <div className="table-header-content">Activity {getSortIcon('activity_score')}</div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleSort('directional_bias')}>
                    <div className="table-header-content">Direction {getSortIcon('directional_bias')}</div>
                  </th>
                  <th className="table-header-cell">
                    <div className="table-header-content">Trader</div>
                  </th>
                  <th className="table-header-cell">
                    <div className="table-header-content">Details</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((chain, idx) => {
                      const biasDisplay = getDirectionalBiasDisplay(chain);
                      return (
                      <motion.tr
                          key={chain.id || idx}
                          className="table-row"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        onClick={() => {
                          setSelectedChain(chain);
                          setShowDetail(true);
                        }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="table-cell"><strong className="ticker-text">{chain.ticker_symbol}</strong></td>
                          <td className="table-cell">
                            {(() => {
                              const optType = chain.type?.toUpperCase() || extractOptionType(chain.option_symbol);
                              return (
                                <span className={`type-badge ${optType === 'CALL' ? 'call' : 'put'}`}>
                                  {optType}
                                </span>
                              );
                            })()}
                        </td>
                          <td className="table-cell">${safeToFixed(chain.strike || extractStrike(chain.option_symbol), 2)}</td>
                          <td className="table-cell premium-cell">{formatCurrency(chain.premium ?? 0)}</td>
                          <td className="table-cell time-cell">{formatLastFillDateTime(chain.last_fill)}</td>
                          <td className="table-cell" style={{ 
                            color: (chain.activity_score ?? 0) > 70 ? '#6ee7b7' : (chain.activity_score ?? 0) > 40 ? '#fbbf24' : '#f87171',
                            fontWeight: 'bold'
                          }}>
                            {safeToFixed(chain.activity_score, 0)}
                        </td>
                          <td className="table-cell">
                            <span className="direction-badge" style={{
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              backgroundColor: biasDisplay.bgColor,
                              color: biasDisplay.color,
                              border: `1px solid ${biasDisplay.color}`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {biasDisplay.emoji} {biasDisplay.text}
                            </span>
                          </td>
                          <td className="table-cell" style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: '16px' }} title={chain.trader_type || 'Unknown'}>
                              {getTraderEmoji(chain.trader_type)}
                            </span>
                          </td>
                          <td className="table-cell">
                          <motion.button
                              className="details-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedChain(chain);
                              setShowDetail(true);
                            }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            →
                          </motion.button>
                        </td>
                      </motion.tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="empty-cell" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        No chains found
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Detail Modal - Single Tab Clean Layout */}
      <AnimatePresence>
        {showDetail && selectedChain && (
          <motion.div
            className="detail-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetail(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
          >
            <motion.div
              className="detail-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#1a1a2e',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                overflow: 'hidden'
              }}
            >
              {/* Modal Header - Fixed at top */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: '#1a1a2e',
                zIndex: 10,
                flexShrink: 0
              }}>
              <button
                onClick={() => setShowDetail(false)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '16px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: '#fff',
                    fontSize: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
              >
                ×
              </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>
                    {selectedChain.ticker_symbol}
                  </span>
              {(() => {
                    const optType = selectedChain.type?.toUpperCase() || extractOptionType(selectedChain.option_symbol);
                return (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: optType === 'CALL' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: optType === 'CALL' ? '#10b981' : '#ef4444'
                      }}>
                        {optType}
                      </span>
                    );
                  })()}
                  <span style={{ color: '#888', fontSize: '14px' }}>
                    ${safeToFixed(selectedChain.strike || extractStrike(selectedChain.option_symbol), 2)}
                  </span>
                          </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {(() => {
                    const expiry = selectedChain.expiry || extractExpiryDate(selectedChain.option_symbol);
                    const dte = selectedChain.dte || calculateDTE(expiry);
                    return `Expiry: ${expiry} (${dte} days) · Stock: $${safeToFixed(selectedChain.stock_price, 2)}`;
                  })()}
                      </div>
                    </div>

              {/* Modal Content - Single Tab Layout - Scrollable area */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                
                {/* DIRECTION Section */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '8px',
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    📊 Direction
                      </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{getDirectionEmoji(selectedChain.directional_bias)}</span>
                    <span style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: (selectedChain.directional_bias || '').toUpperCase() === 'BULLISH' ? '#10b981' : 
                             (selectedChain.directional_bias || '').toUpperCase() === 'BEARISH' ? '#ef4444' : '#9ca3af'
                    }}>
                      {(selectedChain.directional_bias || 'NEUTRAL').toUpperCase()}
                    </span>
                      </div>
                  {selectedChain.direction_reason && (
                    <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                      {selectedChain.direction_reason}
                    </p>
                  )}
                    </div>

                {/* TODAY'S FLOW Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    💰 Today's Flow
                      </div>
              {(() => {
                    // Get contract volumes from ask/bid side (these are the actual traded volumes)
                    const askVolume = selectedChain.ask_side_volume ?? 0;
                    const bidVolume = selectedChain.bid_side_volume ?? 0;
                    const totalVolume = selectedChain.volume ?? (askVolume + bidVolume);
                    
                    // Calculate total premium
                    const totalPremium = typeof selectedChain.premium === 'string' 
                      ? parseFloat(selectedChain.premium) 
                      : (selectedChain.premium ?? 0);
                    
                    // Calculate premium from volume ratios
                    // Ask side = bought (bullish), Bid side = sold (bearish)
                    let premiumBought: number;
                    let premiumSold: number;
                    
                    // Check if new fields exist and have meaningful values
                    if (selectedChain.premium_bought !== undefined && selectedChain.premium_bought > 0) {
                      premiumBought = selectedChain.premium_bought;
                    } else if (totalVolume > 0) {
                      // Calculate from ask/bid volume ratios
                      premiumBought = totalPremium * (askVolume / totalVolume);
                    } else {
                      premiumBought = 0;
                    }
                    
                    if (selectedChain.premium_sold !== undefined && selectedChain.premium_sold > 0) {
                      premiumSold = selectedChain.premium_sold;
                    } else if (totalVolume > 0) {
                      // Calculate from ask/bid volume ratios
                      premiumSold = totalPremium * (bidVolume / totalVolume);
                    } else {
                      premiumSold = 0;
                    }
                    
                    const netFlow = selectedChain.net_flow ?? (premiumBought - premiumSold);
                    
                    // Calculate net flow direction from ask_bias_pct
                    let netFlowDirection = selectedChain.net_flow_direction;
                    if (!netFlowDirection) {
                      // ask_bias_pct: 0 = all selling, 100 = all buying
                      const askBias = selectedChain.ask_bias_pct ?? (totalVolume > 0 ? (askVolume / totalVolume) * 100 : 50);
                      if (askBias >= 65) netFlowDirection = 'STRONG BUYING';
                      else if (askBias >= 55) netFlowDirection = 'BUYING';
                      else if (askBias >= 45) netFlowDirection = 'NEUTRAL';
                      else if (askBias >= 35) netFlowDirection = 'SELLING';
                      else netFlowDirection = 'STRONG SELLING';
                    }
                    
                    return (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        padding: '16px'
                      }}>
                        <div>
                          <div style={{ color: '#888', fontSize: '11px', marginBottom: '4px' }}>Bought (Ask Side)</div>
                          <div style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold' }}>
                            {formatCurrency(premiumBought)}
                    </div>
                          <div style={{ color: '#666', fontSize: '11px' }}>
                            {formatNumber(askVolume)} contracts
                      </div>
                      </div>
                        <div>
                          <div style={{ color: '#888', fontSize: '11px', marginBottom: '4px' }}>Sold (Bid Side)</div>
                          <div style={{ color: '#ef4444', fontSize: '16px', fontWeight: 'bold' }}>
                            {formatCurrency(premiumSold)}
                      </div>
                          <div style={{ color: '#666', fontSize: '11px' }}>
                            {formatNumber(bidVolume)} contracts
                      </div>
                      </div>
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px', marginTop: '4px' }}>
                          <div style={{ color: '#888', fontSize: '11px', marginBottom: '4px' }}>Net Flow</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ 
                              color: getNetFlowColor(netFlowDirection),
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}>
                              {netFlowDirection}
                      </span>
                            <span style={{ color: '#888', fontSize: '13px' }}>
                              ({formatCurrency(netFlow)})
                        </span>
                    </div>
                    </div>
                  </div>
                    );
                  })()}
                </div>

                {/* WHO'S TRADING Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    👥 Who's Trading
                    </div>
                  {(() => {
                    // Use new field or fallback to legacy activity_concentration
                    const traderType = selectedChain.trader_type || selectedChain.activity_concentration || 'Unknown';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{getTraderEmoji(traderType)}</span>
                        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>
                          {traderType}
                        </span>
                    </div>
                    );
                  })()}
                  {selectedChain.trader_insight && (
                    <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                      {selectedChain.trader_insight}
                    </p>
                  )}
                  {selectedChain.trader_breakdown && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      {selectedChain.trader_breakdown.institutional !== undefined && selectedChain.trader_breakdown.institutional > 0 && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          🏛️ {formatNumber(selectedChain.trader_breakdown.institutional)} inst
                        </span>
                      )}
                      {selectedChain.trader_breakdown.hedge_fund !== undefined && selectedChain.trader_breakdown.hedge_fund > 0 && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          🦈 {formatNumber(selectedChain.trader_breakdown.hedge_fund)} HF
                        </span>
                      )}
                      {selectedChain.trader_breakdown.professional !== undefined && selectedChain.trader_breakdown.professional > 0 && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          💼 {formatNumber(selectedChain.trader_breakdown.professional)} pro
                        </span>
                      )}
                      {selectedChain.trader_breakdown.retail !== undefined && selectedChain.trader_breakdown.retail > 0 && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          👤 {formatNumber(selectedChain.trader_breakdown.retail)} retail
                          </span>
                    )}
                  </div>
                  )}
                </div>

                {/* ACTIVITY Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    ⚡ Activity
                </div>
                  {(() => {
                    // Use new field or fallback to legacy unusual_activity_level, or derive from score
                    let activityLevel = selectedChain.activity_level || selectedChain.unusual_activity_level;
                    if (!activityLevel) {
                      const score = selectedChain.activity_score ?? 0;
                      if (score >= 80) activityLevel = 'CRITICAL';
                      else if (score >= 60) activityLevel = 'HIGH';
                      else if (score >= 40) activityLevel = 'MEDIUM';
                      else activityLevel = 'LOW';
                    }
                    
                    return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '16px' }}>{getActivityEmoji(activityLevel)}</span>
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: activityLevel.toUpperCase() === 'CRITICAL' ? '#ef4444' :
                                   activityLevel.toUpperCase() === 'HIGH' ? '#f97316' :
                                   activityLevel.toUpperCase() === 'MEDIUM' ? '#fbbf24' : '#9ca3af'
                          }}>
                            {activityLevel}
                        </span>
                          <span style={{ color: '#888', fontSize: '14px' }}>
                            ({safeToFixed(selectedChain.activity_score, 0)}/100)
                        </span>
                      </div>
                        {selectedChain.activity_insight ? (
                          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                            {selectedChain.activity_insight}
                          </p>
                        ) : (
                          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                            {activityLevel} activity - {formatNumber(selectedChain.trades ?? 0)} trades with {formatCurrency(selectedChain.premium ?? 0)} premium
                          </p>
                        )}
                      </>
                    );
                  })()}
              </div>

                {/* VOLATILITY Section */}
                {selectedChain.iv_insight && (
              <div style={{ marginBottom: '24px' }}>
                    <div style={{ 
                      color: '#6ee7b7',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '8px'
                    }}>
                      📈 Volatility
                    </div>
                    <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                      {selectedChain.iv_insight}
                    </p>
                  </div>
                )}

                {/* POSITIONS (OI) Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    📊 Positions
                    </div>
                  {(() => {
                    const oiCurrent = selectedChain.daily_analysis?.oi_current ?? selectedChain.open_interest ?? 0;
                    const oiChangeAbs = selectedChain.daily_analysis?.oi_change_absolute ?? 0;
                    const oiChangePct = selectedChain.daily_analysis?.oi_change_pct ?? 0;
                    
                    // Priority: 1) New oi_trend field (Dec 14+), 2) Derive from oi_change_pct (fixes old cached data)
                    let oiTrend = selectedChain.oi_trend;
                    if (!oiTrend) {
                      // For old cached data with daily_analysis.oi_position, derive from actual oi_change_pct
                      // This fixes the bug where oi_position was "REDUCING" even when oi_change_pct was 0
                      if (oiChangePct > 5) oiTrend = 'BUILDING';
                      else if (oiChangePct < -5) oiTrend = 'REDUCING';
                      else if (oiChangePct > 0) oiTrend = 'BUILDING';
                      else if (oiChangePct < 0) oiTrend = 'REDUCING';
                      else oiTrend = 'STABLE';
                    }
                    
                    return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px' }}>{getOiTrendEmoji(oiTrend)}</span>
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: oiTrend.toUpperCase() === 'BUILDING' ? '#10b981' :
                                   oiTrend.toUpperCase() === 'REDUCING' ? '#ef4444' : '#9ca3af'
                          }}>
                            {oiTrend.toUpperCase()}
                          </span>
                        </div>
                        {selectedChain.oi_insight ? (
                          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                            {selectedChain.oi_insight}
                          </p>
                        ) : (
                          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                            OI at {formatNumber(oiCurrent)} ({oiChangeAbs >= 0 ? '+' : ''}{formatNumber(oiChangeAbs)}, {oiChangePct >= 0 ? '+' : ''}{safeToFixed(oiChangePct, 1)}%)
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* PATTERNS Section */}
                {selectedChain.patterns && selectedChain.patterns.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ 
                      color: '#6ee7b7',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '12px'
                    }}>
                      🎯 Patterns Detected
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedChain.patterns.map((pattern, idx) => (
                      <span 
                          key={idx}
                        style={{ 
                            padding: '6px 12px',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                          borderRadius: '6px',
                            fontSize: '12px',
                            color: '#6ee7b7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>{patternIcons[pattern] || '🔹'}</span>
                          <span>{pattern.replace(/_/g, ' ')}</span>
                      </span>
                      ))}
                    </div>
                    </div>
                )}

                {/* VOLUME DETAILS Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    📦 Volume Details
                    </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Premium</div>
                      <div style={{ color: '#fbbf24', fontSize: '14px', fontWeight: 'bold' }}>{formatCurrency(selectedChain.premium ?? 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Volume</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{formatNumber(selectedChain.volume ?? 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Trades</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{formatNumber(selectedChain.trades ?? 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Open Interest</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{formatNumber(selectedChain.open_interest ?? 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Sweep Vol</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{formatNumber(selectedChain.sweep_volume ?? 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', marginBottom: '2px' }}>Floor Vol</div>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{formatNumber(selectedChain.floor_volume ?? 0)}</div>
                    </div>
                  </div>
                </div>

                {/* CHAIN INFO Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    color: '#6ee7b7',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    🔗 Chain Info
                    </div>
                        <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '12px'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#888' }}>Symbol: </span>
                      <span style={{ color: '#fff', fontFamily: 'monospace' }}>{selectedChain.option_symbol}</span>
                        </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#888' }}>Delta: </span>
                      <span style={{ color: '#fff' }}>{safeToFixed(selectedChain.delta, 3)}</span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#888' }}>Last Fill: </span>
                      <span style={{ color: '#fff' }}>{formatLastFillDateTime(selectedChain.last_fill)}</span>
                    </div>
                    {selectedChain.next_earnings_date && (
                      <div>
                        <span style={{ color: '#888' }}>Earnings: </span>
                        <span style={{ color: '#fbbf24' }}>{selectedChain.next_earnings_date}</span>
                  </div>
                    )}
                  </div>
                </div>

                </div>

              {/* Close Button - Fixed at bottom */}
              <div style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: '#1a1a2e',
                flexShrink: 0
              }}>
                <motion.button
                  onClick={() => setShowDetail(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                        style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                          borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
              >
                Close Details
              </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!loading && filteredChains.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: 'center', padding: '60px 20px' }}
        >
          <span style={{ fontSize: '48px' }}>📊</span>
          <h3 style={{ color: '#fff', marginTop: '16px' }}>No Data Available</h3>
          <p style={{ color: '#888' }}>Try adjusting your filters or date selection</p>
            <motion.button
              onClick={() => fetchHottestChains()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#10b981',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            >
              Try Again
            </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};
